
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Eye, Edit, Clock, AlertCircle } from "lucide-react";
import { ProjectKanbanBoard } from "@/components/project-tracking/ProjectKanbanBoard";
import { ProjectGanttChart } from "@/components/project-tracking/ProjectGanttChart";

interface SharedProjectData {
  project: {
    id: string;
    name: string;
    description?: string;
    organization_id: string;
  };
  share: {
    id: string;
    permissions: 'view' | 'edit';
    description?: string;
    expires_at?: string;
  };
  phases: any[];
  tasks: any[];
}

const SharedProject = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedProject = async () => {
      if (!token) {
        setError("Token de enlace no válido");
        setLoading(false);
        return;
      }

      try {
        const { data: response, error } = await supabase.functions.invoke('project-share-operations', {
          body: {
            operation: 'get-project-data',
            token,
          },
        });

        if (error) throw error;
        
        if (!response.success) {
          setError(response.error || "Error al cargar el proyecto compartido");
          setLoading(false);
          return;
        }

        setData(response.data);
      } catch (err) {
        console.error('Error fetching shared project:', err);
        setError("Error al cargar el proyecto compartido");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedProject();
  }, [token]);

  const refreshData = async () => {
    if (!token) return;

    try {
      const { data: response, error } = await supabase.functions.invoke('shared-project-operations', {
        body: {
          action: 'getTrackingData',
          shareToken: token,
        },
      });

      if (error) throw error;
      
      if (response.success && data) {
        setData({
          ...data,
          phases: response.phases,
          tasks: response.tasks,
        });
      }
    } catch (err) {
      console.error('Error refreshing shared project data:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {error || "No se pudo cargar el proyecto compartido"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = data.share.expires_at && new Date(data.share.expires_at) < new Date();

  if (isExpired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-destructive" />
              Enlace Expirado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Este enlace de compartir ha expirado y ya no está disponible.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold">{data.project.name}</h1>
              {data.project.description && (
                <p className="text-muted-foreground">{data.project.description}</p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant={data.share.permissions === 'edit' ? 'default' : 'secondary'}>
                {data.share.permissions === 'edit' ? (
                  <>
                    <Edit className="h-3 w-3 mr-1" />
                    Edición
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3 mr-1" />
                    Solo lectura
                  </>
                )}
              </Badge>
              {data.share.expires_at && (
                <Badge variant="outline">
                  <Calendar className="h-3 w-3 mr-1" />
                  Expira: {new Date(data.share.expires_at).toLocaleDateString()}
                </Badge>
              )}
            </div>
          </div>
          
          {data.share.description && (
            <div className="mt-3 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">{data.share.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto py-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <span>Vista compartida del proyecto</span>
            {data.share.permissions === 'edit' ? (
              <span>• Puedes editar tareas</span>
            ) : (
              <span>• Solo visualización</span>
            )}
          </div>
        </div>

        {/* Tabs for Kanban and Gantt views */}
        <Tabs defaultValue="kanban" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
            <TabsTrigger value="gantt">Gantt</TabsTrigger>
          </TabsList>
          
          <TabsContent value="kanban" className="mt-6">
            <ProjectKanbanBoard 
              projectId={data.project.id}
              phases={data.phases}
              tasks={data.tasks}
              onTasksChange={(newTasks) => setData({ ...data, tasks: newTasks })}
              onPhasesChange={(newPhases) => setData({ ...data, phases: newPhases })}
              isSharedView={true}
              canEdit={data.share.permissions === 'edit'}
              shareToken={token}
              organizationId={data.project.organization_id}
              onDataRefresh={refreshData}
            />
          </TabsContent>
          
          <TabsContent value="gantt" className="mt-6">
            <ProjectGanttChart
              projectId={data.project.id}
              phases={data.phases}
              tasks={data.tasks}
              onTasksChange={(newTasks) => setData({ ...data, tasks: newTasks })}
              onPhasesChange={(newPhases) => setData({ ...data, phases: newPhases })}
              userRole={data.share.permissions === 'edit' ? 'salesman' : 'viewer'}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SharedProject;
